import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import apiServices from "../../../services/apiService";
import { primaryColor, secondaryColor } from "../../../utils/comon";
import ToggleSwitch from "../../common/formfields/ToggleSwitch";
import CustomSelect from "../../common/formfields/CustomSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faUser, faCamera, faUpload, faTimes } from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../common/pageheading/PageHeadingSection";
import Breadcrumbs from "../../common/BreadCrum/BreadCrum";

const MyProfile = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isToggleLoading, setIsToggleLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Webcam states
    const [showWebcam, setShowWebcam] = useState(false);
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    const { user: authUser } = useSelector((state) => state.auth);
    const loggedInUserId = authUser?.id;

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        dateOfBirth: "",
        age: "",
        gender: "",
        employeeBloodGroup: "",
        siteId: "",
        wingId: "",
        floorId: "",
        floorId: "",
        unitId: "",
        unitName: "",
        userImage: "",
        visitorAccessRequired: false
    });

    const isUnitAdmin = String(authUser?.roleId) === "4" || String(authUser?.RoleId) === "4";
    const [isUnitUpdating, setIsUnitUpdating] = useState(false);

    const fetchUserDetails = async () => {
        if (!loggedInUserId) return;
        setIsLoading(true);
        try {
            const res = await apiServices({
                method: "GET",
                url: `/CommUserDetails/getUserDetails?Id=${loggedInUserId}&PageNumber=1&PageSize=10`
            });

            const isSuccess = res?.success === true || res?.data?.success === true;
            const resData = res?.data?.Commuserdetailsdata || res?.data?.data || res?.data || [];
            const raw = Array.isArray(resData) ? resData[0] : resData;
            if (raw) {
                setFormData(prev => ({
                    ...prev,
                    firstName: raw.FirstName || "",
                    lastName: raw.LastName || "",
                    email: raw.Email || "",
                    mobileNumber: raw.MobileNumber || "",
                    dateOfBirth: raw.DateOfBirth?.split('T')[0] || "",
                    age: raw.Age || "",
                    gender: raw.Gender || "",
                    employeeBloodGroup: raw.EmployeeBloodGroup || "",
                    userImage: raw.UserImage || "",
                    siteId: raw.siteId || "",
                    wingId: raw.WingId || "",
                    floorId: raw.FloorId || "",
                    unitId: raw.UnitId || "",
                    unitName: raw.UnitName || raw.unitName || "",
                    visitorAccessRequired: raw.AccessId === 1 || raw.accessId === 1
                }));
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUserDetails();
    }, [loggedInUserId]);

    const handleVisitorAccessToggle = (checked) => {
        setFormData(prev => ({ ...prev, visitorAccessRequired: checked }));
    };

    const handleUpdateUnitName = async () => {
        if (!formData.unitId) return toast.warning("Unit ID is missing.");
        if (!formData.unitName || !formData.unitName.trim()) return toast.warning("Unit Name cannot be empty.");

        setIsUnitUpdating(true);
        try {
            // First fetch the existing unit details to ensure we don't overwrite other fields with nulls
            const unitRes = await apiServices({ method: 'GET', url: `/UnitMaster/GetUnitMasterById/${formData.unitId}` });
            if (unitRes.success && unitRes?.data?.data?.[0]) {
                const apiData = unitRes.data.data[0];
                const payload = {
                    siteId: apiData.SiteId,
                    wingId: apiData.WingId,
                    floorId: apiData.FloorId,
                    unitName: formData.unitName,
                    unitNumber: apiData.UnitNumber,
                    isActive: apiData.IsActive === 'Active' || apiData.IsActive === 1 || apiData.IsActive === true ? 1 : 0,
                    accessId: formData.visitorAccessRequired ? 1 : 0,
                    CreatedBy: String(loggedInUserId)
                };

                const response = await apiServices({ method: 'PUT', url: `UnitMaster/UpdateExistingUnitMaster/${formData.unitId}`, data: payload });

                if (response.success) {
                    toast.success(response.data?.Message || "Unit details updated successfully!");
                } else {
                    toast.error(response.data?.Message || "Failed to update unit details.");
                }
            } else {
                toast.error("Could not fetch unit details.");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred while updating unit name.");
        } finally {
            setIsUnitUpdating(false);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [id]: value };

            if (id === "dateOfBirth") {
                if (value) {
                    const birthDate = new Date(value);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                        age--;
                    }
                    updated.age = age >= 0 ? age : "";
                } else {
                    updated.age = ""; // clear age if date is cleared
                }
            }

            return updated;
        });
        if (errors[id]) {
            setErrors(prev => ({ ...prev, [id]: null }));
        }
    };

    const handleSelectChange = (id, option) => {
        const val = option?.value || "";
        setFormData(prev => ({ ...prev, [id]: val }));
        if (errors[id]) {
            setErrors(prev => ({ ...prev, [id]: null }));
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, userImage: reader.result }));
                if (errors.userImage) setErrors(prev => ({ ...prev, userImage: null }));
            };
            reader.readAsDataURL(file);
        }
    };

    const startWebcam = async () => {
        setShowWebcam(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            streamRef.current = stream;
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            }, 100);
        } catch (err) {
            console.error("Error accessing webcam:", err);
            toast.error("Could not access webcam. Please check permissions.");
            setShowWebcam(false);
        }
    };

    const stopWebcam = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setShowWebcam(false);
    };

    const capturePhoto = () => {
        if (videoRef.current) {
            const canvas = document.createElement("canvas");
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL("image/jpeg");
            setFormData(prev => ({ ...prev, userImage: dataUrl }));
            if (errors.userImage) setErrors(prev => ({ ...prev, userImage: null }));
            stopWebcam();
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.userImage) newErrors.userImage = "Photo is required";
        if (!formData.firstName) newErrors.firstName = "First name is required";
        if (!formData.lastName) newErrors.lastName = "Last name is required";
        if (!formData.email) newErrors.email = "Email is required";
        if (!formData.mobileNumber) newErrors.mobileNumber = "Mobile number is required";
        if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
        if (formData.age === "" || formData.age === null) newErrors.age = "Age is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.employeeBloodGroup) newErrors.employeeBloodGroup = "Blood group is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error("Please fill in all mandatory fields.");
            return;
        }

        setIsLoading(true);
        try {
            const url = `/CommUserDetails/putUserDetails/${loggedInUserId}`;
            const method = "PUT";

            const payload = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                mobileNumber: formData.mobileNumber?.toString() || "",
                dateOfBirth: formData.dateOfBirth || null,
                age: formData.age ? Number(formData.age) : null,
                gender: formData.gender,
                employeeBloodGroup: formData.employeeBloodGroup,
                userImage: formData.userImage,
                siteId: formData?.siteId,
                wingId: formData?.wingId,
                floorId: formData?.floorId,
                unitId: formData?.unitId,
                createdBy: String(loggedInUserId)
            };

            const res = await apiServices({ method, url, data: payload });

            if (res?.success) {
                toast.success("Profile updated successfully");
            } else {
                toast.error(res?.message || "Failed to update profile.");
            }
        } catch (error) {
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    const genderOptions = [
        { value: "Male", label: "Male" },
        { value: "Female", label: "Female" },
        { value: "Other", label: "Other" }
    ];

    const bloodGroupOptions = [
        { value: "A+", label: "A+" },
        { value: "A-", label: "A-" },
        { value: "B+", label: "B+" },
        { value: "B-", label: "B-" },
        { value: "O+", label: "O+" },
        { value: "O-", label: "O-" },
        { value: "AB+", label: "AB+" },
        { value: "AB-", label: "AB-" }
    ];

    if (isLoading && !formData.email) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto scrollbar-hide pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="My Profile"
                subHeading="Update your personal information."
            />

            <div className="mt-4 flex-1">
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor shadow-sm relative overflow-hidden">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-secondaryaccent text-primaryaccent border border-accentborder flex items-center justify-center">
                            <FontAwesomeIcon icon={faUser} className="text-lg" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">Personal Information</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Profile Photo */}
                        <div className="lg:row-span-2 flex flex-col items-center">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-2 w-full text-center">
                                Profile Photo <span className="text-red-500">*</span>
                            </label>
                            <div className="relative group w-32 h-32 mb-3">
                                <div className="absolute inset-0 w-full h-full rounded-full border-2 border-dashed border-gray-300 dark:border-bordercolor flex items-center justify-center bg-gray-50 dark:bg-actionitembg">
                                    <FontAwesomeIcon icon={faUser} className="text-4xl text-gray-300" />
                                </div>
                                {formData.userImage && formData.userImage !== 'null' && formData.userImage !== 'undefined' && formData.userImage.trim() !== '' && (
                                    <div className="absolute inset-0 w-full h-full rounded-full overflow-hidden border-2 border-gray-200 dark:border-bordercolor z-10 bg-white">
                                        <img 
                                            src={formData.userImage} 
                                            alt="Profile" 
                                            className="w-full h-full object-cover" 
                                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                        />
                                    </div>
                                )}
                            </div>
                            {errors.userImage && <p className="text-red-500 text-[10px] mt-1 mb-2">{errors.userImage}</p>}
                            <div className="flex gap-2 w-full justify-center">
                                <label className="flex-1 cursor-pointer bg-blue-50 text-blue-600 px-3 py-2 rounded-lg text-xs font-bold hover:bg-blue-100 transition-colors text-center border border-blue-100">
                                    <FontAwesomeIcon icon={faUpload} className="mr-1" /> Upload
                                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                </label>
                                <button
                                    onClick={startWebcam}
                                    className="flex-1 bg-emerald-50 text-emerald-600 px-3 py-2 rounded-lg text-xs font-bold hover:bg-emerald-100 transition-colors border border-emerald-100"
                                >
                                    <FontAwesomeIcon icon={faCamera} className="mr-1" /> Capture
                                </button>
                            </div>
                        </div>

                        {/* Text Fields */}
                        <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="firstName">
                                    First Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.firstName ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`}
                                    type="text"
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    placeholder="Enter first name"
                                />
                                {errors.firstName && <p className="text-red-500 text-[10px] mt-1">{errors.firstName}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="lastName">
                                    Last Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.lastName ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`}
                                    type="text"
                                    id="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    placeholder="Enter last name"
                                />
                                {errors.lastName && <p className="text-red-500 text-[10px] mt-1">{errors.lastName}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="email">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.email ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`}
                                    type="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="name@example.com"
                                />
                                {errors.email && <p className="text-red-500 text-[10px] mt-1">{errors.email}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="mobileNumber">
                                    Mobile Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.mobileNumber ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`}
                                    type="text"
                                    id="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, "");
                                        if (val.length <= 10) {
                                            handleInputChange({ target: { id: "mobileNumber", value: val } });
                                        }
                                    }}
                                    placeholder="10-digit mobile number"
                                />
                                {errors.mobileNumber && <p className="text-red-500 text-[10px] mt-1">{errors.mobileNumber}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="dateOfBirth">
                                    Date of Birth <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`}
                                    type="date"
                                    id="dateOfBirth"
                                    value={formData.dateOfBirth}
                                    onChange={handleInputChange}
                                    max={new Date().toISOString().split("T")[0]}
                                />
                                {errors.dateOfBirth && <p className="text-red-500 text-[10px] mt-1">{errors.dateOfBirth}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="age">
                                    Age <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.age ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`}
                                    type="text"
                                    id="age"
                                    value={formData.age}
                                    readOnly
                                    placeholder="e.g. 30"
                                />
                                {errors.age && <p className="text-red-500 text-[10px] mt-1">{errors.age}</p>}
                            </div>

                            <div>
                                <CustomSelect
                                    label={<>GENDER <span className="text-red-500">*</span></>}
                                    required={false}
                                    options={genderOptions}
                                    value={genderOptions.find((o) => o.value === formData.gender) || null}
                                    onChange={(opt) => handleSelectChange("gender", opt)}
                                    error={!!errors.gender}
                                />
                                {errors.gender && <p className="text-red-500 text-[10px] mt-1">{errors.gender}</p>}
                            </div>

                            <div>
                                <CustomSelect
                                    label={<>BLOOD GROUP <span className="text-red-500">*</span></>}
                                    required={false}
                                    options={bloodGroupOptions}
                                    value={bloodGroupOptions.find((o) => o.value === formData.employeeBloodGroup) || null}
                                    onChange={(opt) => handleSelectChange("employeeBloodGroup", opt)}
                                    error={!!errors.employeeBloodGroup}
                                />
                                {errors.employeeBloodGroup && <p className="text-red-500 text-[10px] mt-1">{errors.employeeBloodGroup}</p>}
                            </div>
                        </div>
                    </div>

                    {isUnitAdmin && (
                        <>
                            <div className="mt-6 pt-6 border-t border-gray-100 dark:border-bordercolor flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-bold text-gray-800 dark:text-textprimary">Unit Details</h4>
                                    <p className="text-xs text-gray-500 dark:text-textsecondary mt-1">Manage your unit information.</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="unitName">
                                        Unit Name
                                    </label>
                                    <input
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                        type="text"
                                        id="unitName"
                                        value={formData.unitName}
                                        onChange={handleInputChange}
                                        placeholder="e.g. A-101"
                                    />
                                </div>
                            </div>

                            <div className="mt-6 pt-6 border-t border-gray-100 dark:border-bordercolor flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-bold text-gray-800 dark:text-textprimary">Visitor Access Control</h4>
                                    <p className="text-xs text-gray-500 dark:text-textsecondary mt-1">Enable this to require your approval before visitors can enter.</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <ToggleSwitch
                                        label="Require Host Approval"
                                        checked={formData.visitorAccessRequired}
                                        onChange={handleVisitorAccessToggle}
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex items-end">
                                <button
                                    onClick={handleUpdateUnitName}
                                    disabled={isUnitUpdating}
                                    className="px-4 py-2 rounded-lg text-xs font-bold text-white transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center min-w-[120px]"
                                    style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                                        e.currentTarget.style.color = '#FFFFFF';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                                        e.currentTarget.style.color = "var(--color-primaryaccent)";
                                    }}
                                >
                                    {isUnitUpdating ? <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" /> : null}
                                    Update Unit Details
                                </button>
                            </div>
                        </>
                    )}

                    <div className="mt-8 flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-bordercolor">
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="px-6 py-2.5 rounded-lg text-sm font-bold text-white transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center min-w-[120px]"
                            style={{ backgroundColor: primaryColor() }}
                        >
                            {isLoading ? (
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            ) : (
                                "Update Profile"
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Webcam Modal */}
            {showWebcam && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-cardbg rounded-2xl p-6 w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-textprimary">Capture Photo</h3>
                            <button onClick={stopWebcam} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <div className="relative rounded-xl overflow-hidden bg-gray-900 aspect-video mb-4 shadow-inner">
                            <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover transform scale-x-[-1]" />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={stopWebcam} className="px-5 py-2 text-sm font-bold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                                Cancel
                            </button>
                            <button onClick={capturePhoto} className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2">
                                <FontAwesomeIcon icon={faCamera} /> Capture
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyProfile;
